import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Settings, Save, Loader2, AlertTriangle, Check, X, ChevronDown, ChevronUp,
  FileText, MapPin, Calendar, Image, Type, Info, Clock, Users, Ban,
  PoundSterling, Hotel, Bus, CreditCard, CalendarClock, Globe, Code, Copy,
  RefreshCw, Wrench, Trash2, Eye, EyeOff, AlertCircle, ExternalLink
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Switch } from '../../ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../ui/alert-dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../ui/collapsible';
import { toast } from 'sonner';
import { cn } from '../../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============== UTILITY FUNCTIONS ==============

const formatDateForInput = (dateStr) => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    return date.toISOString().split('T')[0];
  } catch {
    return '';
  }
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP'
  }).format(amount || 0);
};

// ============== SECTION WRAPPER COMPONENT ==============

const SettingsSection = ({ 
  id,
  title, 
  description, 
  icon: Icon, 
  children, 
  hasChanges, 
  saving, 
  onSave,
  defaultOpen = false,
  variant = 'default',
  saveDisabled = false
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  const variants = {
    default: 'border-slate-200',
    warning: 'border-orange-200 bg-orange-50/30',
    danger: 'border-red-200 bg-red-50/30'
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card className={cn("transition-all", variants[variant])}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-slate-50/50 transition-colors">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {Icon && (
                  <div className={cn(
                    "w-10 h-10 rounded-lg flex items-center justify-center",
                    variant === 'danger' ? "bg-red-100" : variant === 'warning' ? "bg-orange-100" : "bg-violet-100"
                  )}>
                    <Icon className={cn(
                      "w-5 h-5",
                      variant === 'danger' ? "text-red-600" : variant === 'warning' ? "text-orange-600" : "text-violet-600"
                    )} />
                  </div>
                )}
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    {title}
                    {hasChanges && (
                      <Badge variant="outline" className="text-orange-600 border-orange-300 bg-orange-50 text-xs">
                        Unsaved
                      </Badge>
                    )}
                  </CardTitle>
                  {description && (
                    <CardDescription className="text-sm mt-0.5">{description}</CardDescription>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {hasChanges && onSave && (
                  <Button
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSave();
                    }}
                    disabled={saving || saveDisabled}
                    className="bg-violet-600 hover:bg-violet-700"
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-1" />
                    )}
                    Save
                  </Button>
                )}
                {isOpen ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0 pb-5">
            {children}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

// ============== WARNING BANNER COMPONENT ==============

const WarningBanner = ({ children, variant = 'warning' }) => {
  const variants = {
    warning: 'bg-orange-50 border-orange-200 text-orange-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800',
    danger: 'bg-red-50 border-red-200 text-red-800'
  };
  
  return (
    <div className={cn("border rounded-lg p-3 flex items-start gap-2 mb-4", variants[variant])}>
      <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
      <div className="text-sm">{children}</div>
    </div>
  );
};

// ============== MAIN COMPONENT ==============

export default function WeekenderSettings({ 
  event, 
  bookings,
  authToken,
  onRefresh,
  onEventUpdated
}) {
  // Track original event data for comparison
  const [originalEvent, setOriginalEvent] = useState(null);
  
  // Section-level form states
  const [generalForm, setGeneralForm] = useState({});
  const [bookingForm, setBookingForm] = useState({});
  const [pricingForm, setPricingForm] = useState({});
  const [roomsForm, setRoomsForm] = useState({ room_options: [] });
  const [ticketsForm, setTicketsForm] = useState({ ticket_options: [] });
  const [addOnsForm, setAddOnsForm] = useState({ add_ons: [] });
  const [paymentPlanForm, setPaymentPlanForm] = useState({});
  const [publishingForm, setPublishingForm] = useState({});
  
  // Saving states per section
  const [savingSection, setSavingSection] = useState(null);
  
  // Embed codes
  const [embedCodes, setEmbedCodes] = useState(null);
  const [loadingEmbed, setLoadingEmbed] = useState(false);
  
  // Diagnostics
  const [diagnostics, setDiagnostics] = useState(null);
  const [loadingDiagnostics, setLoadingDiagnostics] = useState(false);
  
  // Confirmation dialogs
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: null, data: null });
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteInput, setDeleteInput] = useState('');
  
  // Count existing bookings for warnings
  const activeBookingsCount = useMemo(() => {
    return (bookings || []).filter(b => b.status !== 'cancelled' && b.status !== 'refunded').length;
  }, [bookings]);

  const hasBookings = activeBookingsCount > 0;

  // ============== INITIALIZE FORM DATA ==============

  useEffect(() => {
    if (event) {
      setOriginalEvent(event);
      
      // General
      setGeneralForm({
        title: event.title || '',
        slug: event.slug || '',
        description: event.description || '',
        venue_name: event.venue_name || '',
        venue_address: event.venue_address || '',
        start_date: formatDateForInput(event.start_date),
        end_date: formatDateForInput(event.end_date),
        image_url: event.image_url || '',
        event_type: event.event_type || 'dance_holiday',
        additional_info: event.additional_info || ''
      });
      
      // Booking
      setBookingForm({
        booking_deadline: formatDateForInput(event.booking_deadline),
        max_capacity: event.max_capacity || '',
        no_deposit_option: event.no_deposit_option || false
      });
      
      // Pricing
      setPricingForm({
        room_deposit_amount: event.room_deposit_amount ?? 50,
        pitch_deposit_amount: event.pitch_deposit_amount ?? 25
      });
      
      // Rooms
      setRoomsForm({
        room_options: (event.room_options || []).map(r => ({
          type: r.type || '',
          category: r.category || 'room',
          pricing_type: r.pricing_type || 'per_person',
          price_per_person: r.price_per_person ?? 0,
          price_per_unit: r.price_per_unit ?? 0,
          description: r.description || '',
          available_count: r.available_count ?? null,
          booked_count: r.booked_count ?? 0,
          max_occupancy: r.max_occupancy ?? null,
          min_occupancy: r.min_occupancy ?? 1,
          includes_ticket: r.includes_ticket || false,
          amenities: r.amenities || [],
          capacity: r.capacity ?? null
        }))
      });
      
      // Tickets
      setTicketsForm({
        ticket_options: (event.ticket_options || []).map(t => ({
          type: t.type || '',
          price: t.price ?? 0,
          description: t.description || '',
          available_count: t.available_count ?? null,
          booked_count: t.booked_count ?? 0,
          valid_dates: t.valid_dates || []
        }))
      });
      
      // Add-ons
      setAddOnsForm({
        add_ons: (event.add_ons || []).map(a => ({
          name: a.name || '',
          price: a.price ?? 0,
          deposit_amount: a.deposit_amount ?? 0,
          description: a.description || ''
        }))
      });
      
      // Payment Plans
      setPaymentPlanForm({
        payment_plan_start_date: formatDateForInput(event.payment_plan_start_date),
        payment_plan_end_date: formatDateForInput(event.payment_plan_end_date)
      });
      
      // Publishing
      setPublishingForm({
        is_published: event.is_published || false
      });
    }
  }, [event]);

  // ============== CHANGE DETECTION ==============

  const hasGeneralChanges = useMemo(() => {
    if (!originalEvent) return false;
    return (
      generalForm.title !== (originalEvent.title || '') ||
      generalForm.slug !== (originalEvent.slug || '') ||
      generalForm.description !== (originalEvent.description || '') ||
      generalForm.venue_name !== (originalEvent.venue_name || '') ||
      generalForm.venue_address !== (originalEvent.venue_address || '') ||
      generalForm.start_date !== formatDateForInput(originalEvent.start_date) ||
      generalForm.end_date !== formatDateForInput(originalEvent.end_date) ||
      generalForm.image_url !== (originalEvent.image_url || '') ||
      generalForm.event_type !== (originalEvent.event_type || 'dance_holiday') ||
      generalForm.additional_info !== (originalEvent.additional_info || '')
    );
  }, [generalForm, originalEvent]);

  const hasBookingChanges = useMemo(() => {
    if (!originalEvent) return false;
    return (
      bookingForm.booking_deadline !== formatDateForInput(originalEvent.booking_deadline) ||
      String(bookingForm.max_capacity || '') !== String(originalEvent.max_capacity || '') ||
      bookingForm.no_deposit_option !== (originalEvent.no_deposit_option || false)
    );
  }, [bookingForm, originalEvent]);

  const hasPricingChanges = useMemo(() => {
    if (!originalEvent) return false;
    return (
      Number(pricingForm.room_deposit_amount) !== (originalEvent.room_deposit_amount ?? 50) ||
      Number(pricingForm.pitch_deposit_amount) !== (originalEvent.pitch_deposit_amount ?? 25)
    );
  }, [pricingForm, originalEvent]);

  const hasRoomsChanges = useMemo(() => {
    if (!originalEvent) return false;
    const original = JSON.stringify(originalEvent.room_options || []);
    const current = JSON.stringify(roomsForm.room_options);
    return original !== current;
  }, [roomsForm, originalEvent]);

  const hasTicketsChanges = useMemo(() => {
    if (!originalEvent) return false;
    const original = JSON.stringify(originalEvent.ticket_options || []);
    const current = JSON.stringify(ticketsForm.ticket_options);
    return original !== current;
  }, [ticketsForm, originalEvent]);

  const hasAddOnsChanges = useMemo(() => {
    if (!originalEvent) return false;
    const original = JSON.stringify(originalEvent.add_ons || []);
    const current = JSON.stringify(addOnsForm.add_ons);
    return original !== current;
  }, [addOnsForm, originalEvent]);

  const hasPaymentPlanChanges = useMemo(() => {
    if (!originalEvent) return false;
    return (
      paymentPlanForm.payment_plan_start_date !== formatDateForInput(originalEvent.payment_plan_start_date) ||
      paymentPlanForm.payment_plan_end_date !== formatDateForInput(originalEvent.payment_plan_end_date)
    );
  }, [paymentPlanForm, originalEvent]);

  const hasPublishingChanges = useMemo(() => {
    if (!originalEvent) return false;
    return publishingForm.is_published !== (originalEvent.is_published || false);
  }, [publishingForm, originalEvent]);

  // ============== SAVE HANDLERS ==============

  const saveSection = async (section, data) => {
    setSavingSection(section);
    try {
      const response = await fetch(
        `${API_URL}/api/weekends/admin/events/${event.event_id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify(data)
        }
      );

      if (response.ok) {
        const updated = await response.json();
        toast.success(`${section} settings saved successfully`);
        if (onEventUpdated) onEventUpdated(updated);
        if (onRefresh) onRefresh();
        return true;
      } else {
        const error = await response.json();
        toast.error(error.detail || `Failed to save ${section} settings`);
        return false;
      }
    } catch (error) {
      toast.error(`Failed to save ${section} settings`);
      return false;
    } finally {
      setSavingSection(null);
    }
  };

  const saveGeneral = async () => {
    const data = {
      title: generalForm.title,
      slug: generalForm.slug || undefined,
      description: generalForm.description,
      venue_name: generalForm.venue_name,
      venue_address: generalForm.venue_address,
      start_date: generalForm.start_date ? new Date(generalForm.start_date).toISOString() : undefined,
      end_date: generalForm.end_date ? new Date(generalForm.end_date).toISOString() : undefined,
      image_url: generalForm.image_url || undefined,
      event_type: generalForm.event_type,
      additional_info: generalForm.additional_info || undefined
    };
    await saveSection('General', data);
  };

  const saveBooking = async () => {
    const data = {
      booking_deadline: bookingForm.booking_deadline ? new Date(bookingForm.booking_deadline).toISOString() : null,
      max_capacity: bookingForm.max_capacity ? parseInt(bookingForm.max_capacity) : null
    };
    
    // Save main booking settings
    const success = await saveSection('Booking', data);
    
    // Also update no_deposit_option via separate endpoint if changed
    if (success && bookingForm.no_deposit_option !== (originalEvent?.no_deposit_option || false)) {
      try {
        await fetch(
          `${API_URL}/api/weekends/admin/events/${event.event_id}/no-deposit?enabled=${bookingForm.no_deposit_option}`,
          {
            method: 'PATCH',
            headers: { 'Authorization': `Bearer ${authToken}` }
          }
        );
      } catch (error) {
        console.error('Failed to update no-deposit option:', error);
      }
    }
  };

  const savePricing = async () => {
    if (hasBookings) {
      setConfirmDialog({
        open: true,
        type: 'pricing',
        data: {
          room_deposit_amount: parseFloat(pricingForm.room_deposit_amount) || 50,
          pitch_deposit_amount: parseFloat(pricingForm.pitch_deposit_amount) || 25
        }
      });
    } else {
      await saveSection('Pricing', {
        room_deposit_amount: parseFloat(pricingForm.room_deposit_amount) || 50,
        pitch_deposit_amount: parseFloat(pricingForm.pitch_deposit_amount) || 25
      });
    }
  };

  const saveRooms = async () => {
    // Check for availability reduction warnings
    const hasAvailabilityIssue = roomsForm.room_options.some(room => {
      const booked = room.booked_count || 0;
      const available = room.available_count;
      return available !== null && available < booked;
    });

    if (hasAvailabilityIssue || (hasBookings && hasRoomsChanges)) {
      setConfirmDialog({
        open: true,
        type: 'rooms',
        data: { room_options: roomsForm.room_options }
      });
    } else {
      await saveSection('Rooms', { room_options: roomsForm.room_options });
    }
  };

  const saveTickets = async () => {
    await saveSection('Tickets', { ticket_options: ticketsForm.ticket_options });
  };

  const saveAddOns = async () => {
    if (hasBookings && hasAddOnsChanges) {
      setConfirmDialog({
        open: true,
        type: 'addons',
        data: { add_ons: addOnsForm.add_ons }
      });
    } else {
      await saveSection('Add-ons', { add_ons: addOnsForm.add_ons });
    }
  };

  const savePaymentPlan = async () => {
    if (hasBookings && hasPaymentPlanChanges) {
      setConfirmDialog({
        open: true,
        type: 'paymentplan',
        data: {
          payment_plan_start_date: paymentPlanForm.payment_plan_start_date ? new Date(paymentPlanForm.payment_plan_start_date).toISOString() : null,
          payment_plan_end_date: paymentPlanForm.payment_plan_end_date ? new Date(paymentPlanForm.payment_plan_end_date).toISOString() : null
        }
      });
    } else {
      await saveSection('Payment Plans', {
        payment_plan_start_date: paymentPlanForm.payment_plan_start_date ? new Date(paymentPlanForm.payment_plan_start_date).toISOString() : null,
        payment_plan_end_date: paymentPlanForm.payment_plan_end_date ? new Date(paymentPlanForm.payment_plan_end_date).toISOString() : null
      });
    }
  };

  const savePublishing = async () => {
    // Warn if unpublishing with bookings
    if (!publishingForm.is_published && originalEvent?.is_published && hasBookings) {
      setConfirmDialog({
        open: true,
        type: 'unpublish',
        data: { is_published: false }
      });
    } else {
      await saveSection('Publishing', { is_published: publishingForm.is_published });
    }
  };

  const executeConfirmedSave = async () => {
    const { type, data } = confirmDialog;
    setConfirmDialog({ open: false, type: null, data: null });
    
    switch (type) {
      case 'pricing':
        await saveSection('Pricing', data);
        break;
      case 'rooms':
        await saveSection('Rooms', data);
        break;
      case 'addons':
        await saveSection('Add-ons', data);
        break;
      case 'paymentplan':
        await saveSection('Payment Plans', data);
        break;
      case 'unpublish':
        await saveSection('Publishing', data);
        break;
      default:
        break;
    }
  };

  // ============== EMBED CODES ==============

  const fetchEmbedCodes = async () => {
    setLoadingEmbed(true);
    try {
      const response = await fetch(
        `${API_URL}/api/weekends/${event.event_id}/embed-code`,
        { headers: { 'Authorization': `Bearer ${authToken}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setEmbedCodes(data);
      }
    } catch (error) {
      toast.error('Failed to load embed codes');
    } finally {
      setLoadingEmbed(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  // ============== DIAGNOSTICS ==============

  const fetchDiagnostics = async () => {
    setLoadingDiagnostics(true);
    try {
      const response = await fetch(
        `${API_URL}/api/weekends/admin/events/${event.event_id}/diagnostics`,
        { headers: { 'Authorization': `Bearer ${authToken}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setDiagnostics(data);
      }
    } catch (error) {
      toast.error('Failed to load diagnostics');
    } finally {
      setLoadingDiagnostics(false);
    }
  };

  const recalculateAvailability = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/weekends/admin/events/${event.event_id}/recalculate-availability`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${authToken}` }
        }
      );
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || 'Availability recalculated');
        if (onRefresh) onRefresh();
      } else {
        toast.error('Failed to recalculate availability');
      }
    } catch (error) {
      toast.error('Failed to recalculate availability');
    }
  };

  // ============== DELETE EVENT ==============

  const handleDeleteEvent = async () => {
    if (deleteInput !== event.title) {
      toast.error('Please type the event title exactly to confirm deletion');
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/api/weekends/admin/events/${event.event_id}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${authToken}` }
        }
      );

      if (response.ok) {
        toast.success('Event deleted successfully');
        setDeleteConfirm(false);
        // Navigate back - this will need to be handled by parent
        if (onEventUpdated) onEventUpdated(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete event');
      }
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  // ============== ROOM MANAGEMENT HELPERS ==============

  const addRoomOption = () => {
    setRoomsForm(prev => ({
      room_options: [...prev.room_options, {
        type: '',
        category: 'room',
        pricing_type: 'per_person',
        price_per_person: 0,
        price_per_unit: 0,
        description: '',
        available_count: null,
        booked_count: 0,
        max_occupancy: null,
        min_occupancy: 1,
        includes_ticket: false,
        amenities: [],
        capacity: null
      }]
    }));
  };

  const updateRoomOption = (index, field, value) => {
    setRoomsForm(prev => {
      const updated = [...prev.room_options];
      updated[index] = { ...updated[index], [field]: value };
      
      // Auto-set pricing_type based on category
      if (field === 'category') {
        if (value === 'camping') {
          updated[index].pricing_type = 'per_unit';
        } else if (value === 'room') {
          updated[index].pricing_type = 'per_person';
        }
      }
      
      return { room_options: updated };
    });
  };

  const removeRoomOption = (index) => {
    setRoomsForm(prev => ({
      room_options: prev.room_options.filter((_, i) => i !== index)
    }));
  };

  // ============== TICKET MANAGEMENT HELPERS ==============

  const addTicketOption = () => {
    setTicketsForm(prev => ({
      ticket_options: [...prev.ticket_options, {
        type: '',
        price: 0,
        description: '',
        available_count: null,
        booked_count: 0,
        valid_dates: []
      }]
    }));
  };

  const updateTicketOption = (index, field, value) => {
    setTicketsForm(prev => {
      const updated = [...prev.ticket_options];
      updated[index] = { ...updated[index], [field]: value };
      return { ticket_options: updated };
    });
  };

  const removeTicketOption = (index) => {
    setTicketsForm(prev => ({
      ticket_options: prev.ticket_options.filter((_, i) => i !== index)
    }));
  };

  // ============== ADD-ON MANAGEMENT HELPERS ==============

  const addAddOn = () => {
    setAddOnsForm(prev => ({
      add_ons: [...prev.add_ons, {
        name: '',
        price: 0,
        deposit_amount: 0,
        description: ''
      }]
    }));
  };

  const updateAddOn = (index, field, value) => {
    setAddOnsForm(prev => {
      const updated = [...prev.add_ons];
      updated[index] = { ...updated[index], [field]: value };
      return { add_ons: updated };
    });
  };

  const removeAddOn = (index) => {
    setAddOnsForm(prev => ({
      add_ons: prev.add_ons.filter((_, i) => i !== index)
    }));
  };

  // ============== RENDER ==============

  if (!event) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Settings</h2>
          <p className="text-sm text-slate-500">Configure event details, pricing, and options</p>
        </div>
        {hasBookings && (
          <Badge variant="outline" className="text-blue-600 border-blue-200 bg-blue-50">
            <Users className="w-3 h-3 mr-1" />
            {activeBookingsCount} active booking{activeBookingsCount !== 1 ? 's' : ''}
          </Badge>
        )}
      </div>

      {/* Sections */}
      <div className="space-y-3">
        
        {/* ============== 1. GENERAL ============== */}
        <SettingsSection
          id="general"
          title="General"
          description="Basic event information"
          icon={FileText}
          hasChanges={hasGeneralChanges}
          saving={savingSection === 'General'}
          onSave={saveGeneral}
          defaultOpen={true}
        >
          {hasBookings && (generalForm.start_date !== formatDateForInput(originalEvent?.start_date) || 
                          generalForm.end_date !== formatDateForInput(originalEvent?.end_date)) && (
            <WarningBanner>
              Changing event dates may confuse existing customers. Consider sending a notification.
            </WarningBanner>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>Event Title *</Label>
              <Input
                value={generalForm.title}
                onChange={(e) => setGeneralForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g. Spring Dance Weekend 2026"
              />
            </div>
            
            <div>
              <Label>URL Slug</Label>
              <Input
                value={generalForm.slug}
                onChange={(e) => setGeneralForm(prev => ({ 
                  ...prev, 
                  slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-')
                }))}
                placeholder="auto-generated-from-title"
              />
              {generalForm.slug && (
                <p className="text-xs text-slate-500 mt-1">
                  URL: /weekends/<span className="font-medium text-violet-600">{generalForm.slug}</span>
                </p>
              )}
              {hasBookings && generalForm.slug !== originalEvent?.slug && (
                <p className="text-xs text-orange-600 mt-1">
                  ⚠️ Changing the slug may break existing links shared with customers
                </p>
              )}
            </div>
            
            <div>
              <Label>Event Type</Label>
              <Select 
                value={generalForm.event_type} 
                onValueChange={(v) => setGeneralForm(prev => ({ ...prev, event_type: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="dance_holiday">Dance Holiday</SelectItem>
                  <SelectItem value="festival">Festival</SelectItem>
                  <SelectItem value="day_event">Day Event</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Venue Name *</Label>
              <Input
                value={generalForm.venue_name}
                onChange={(e) => setGeneralForm(prev => ({ ...prev, venue_name: e.target.value }))}
                placeholder="e.g. The Grand Hotel"
              />
            </div>
            
            <div>
              <Label>Venue Address *</Label>
              <Input
                value={generalForm.venue_address}
                onChange={(e) => setGeneralForm(prev => ({ ...prev, venue_address: e.target.value }))}
                placeholder="Full address"
              />
            </div>
            
            <div>
              <Label>Start Date *</Label>
              <Input
                type="date"
                value={generalForm.start_date}
                onChange={(e) => setGeneralForm(prev => ({ ...prev, start_date: e.target.value }))}
              />
            </div>
            
            <div>
              <Label>End Date *</Label>
              <Input
                type="date"
                value={generalForm.end_date}
                onChange={(e) => setGeneralForm(prev => ({ ...prev, end_date: e.target.value }))}
              />
            </div>
            
            <div className="md:col-span-2">
              <Label>Image URL</Label>
              <Input
                value={generalForm.image_url}
                onChange={(e) => setGeneralForm(prev => ({ ...prev, image_url: e.target.value }))}
                placeholder="https://..."
              />
            </div>
            
            <div className="md:col-span-2">
              <Label>Description *</Label>
              <Textarea
                value={generalForm.description}
                onChange={(e) => setGeneralForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe your event..."
                rows={4}
              />
            </div>
            
            <div className="md:col-span-2">
              <Label>Additional Information</Label>
              <Textarea
                value={generalForm.additional_info}
                onChange={(e) => setGeneralForm(prev => ({ ...prev, additional_info: e.target.value }))}
                placeholder="Cancellation policy, parking info, etc."
                rows={3}
              />
            </div>
          </div>
        </SettingsSection>

        {/* ============== 2. BOOKING SETTINGS ============== */}
        <SettingsSection
          id="booking"
          title="Booking Settings"
          description="Control booking availability and options"
          icon={Calendar}
          hasChanges={hasBookingChanges}
          saving={savingSection === 'Booking'}
          onSave={saveBooking}
        >
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Booking Deadline</Label>
                <Input
                  type="date"
                  value={bookingForm.booking_deadline}
                  onChange={(e) => setBookingForm(prev => ({ ...prev, booking_deadline: e.target.value }))}
                />
                <p className="text-xs text-slate-500 mt-1">
                  Bookings close automatically after this date
                </p>
              </div>
              
              <div>
                <Label>Maximum Capacity</Label>
                <Input
                  type="number"
                  value={bookingForm.max_capacity}
                  onChange={(e) => setBookingForm(prev => ({ ...prev, max_capacity: e.target.value }))}
                  placeholder="Unlimited"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Leave empty for unlimited capacity
                </p>
              </div>
            </div>
            
            <div className="pt-2 border-t">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base">No-Deposit Option</Label>
                  <p className="text-sm text-slate-500 mt-0.5">
                    Allow logged-in users to book without paying a deposit
                  </p>
                </div>
                <Switch
                  checked={bookingForm.no_deposit_option}
                  onCheckedChange={(checked) => setBookingForm(prev => ({ ...prev, no_deposit_option: checked }))}
                />
              </div>
              
              {bookingForm.no_deposit_option && (
                <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <p className="text-sm text-emerald-800">
                    <strong>Promotion Active:</strong> Logged-in users can book without paying a deposit. 
                    They'll receive a booking confirmation and can pay the full amount later.
                  </p>
                </div>
              )}
            </div>
          </div>
        </SettingsSection>

        {/* ============== 3. PRICING & DEPOSITS ============== */}
        <SettingsSection
          id="pricing"
          title="Pricing & Deposits"
          description="Configure deposit amounts for new bookings"
          icon={PoundSterling}
          hasChanges={hasPricingChanges}
          saving={savingSection === 'Pricing'}
          onSave={savePricing}
        >
          {hasBookings && hasPricingChanges && (
            <WarningBanner>
              Changes to deposit amounts only affect <strong>new bookings</strong>. 
              Existing bookings ({activeBookingsCount}) will not be recalculated.
            </WarningBanner>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Room Deposit Amount (£/person)</Label>
              <Input
                type="number"
                value={pricingForm.room_deposit_amount}
                onChange={(e) => setPricingForm(prev => ({ ...prev, room_deposit_amount: e.target.value }))}
                min="0"
                step="0.01"
              />
              <p className="text-xs text-slate-500 mt-1">
                Fixed deposit required per person for room bookings
              </p>
            </div>
            
            <div>
              <Label>Pitch Deposit Amount (£/pitch)</Label>
              <Input
                type="number"
                value={pricingForm.pitch_deposit_amount}
                onChange={(e) => setPricingForm(prev => ({ ...prev, pitch_deposit_amount: e.target.value }))}
                min="0"
                step="0.01"
              />
              <p className="text-xs text-slate-500 mt-1">
                Fixed deposit required per pitch for camping bookings
              </p>
            </div>
          </div>
        </SettingsSection>

        {/* ============== 4. ROOMS CONFIGURATION ============== */}
        <SettingsSection
          id="rooms"
          title="Rooms Configuration"
          description="Configure room types and pricing"
          icon={Hotel}
          hasChanges={hasRoomsChanges}
          saving={savingSection === 'Rooms'}
          onSave={saveRooms}
        >
          {hasBookings && hasRoomsChanges && (
            <WarningBanner>
              Price changes only affect <strong>new bookings</strong>. 
              Existing bookings will retain their original pricing.
            </WarningBanner>
          )}
          
          <div className="space-y-4">
            <div className="flex justify-end">
              <Button variant="outline" size="sm" onClick={addRoomOption}>
                <Hotel className="w-4 h-4 mr-1" /> Add Room Type
              </Button>
            </div>
            
            {roomsForm.room_options.length === 0 ? (
              <div className="text-center py-8 bg-slate-50 rounded-lg">
                <Hotel className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                <p className="text-slate-600">No room types configured</p>
                <p className="text-sm text-slate-500">Add room types for customers to book</p>
              </div>
            ) : (
              <div className="space-y-3">
                {roomsForm.room_options.map((room, idx) => {
                  const availabilityWarning = room.available_count !== null && 
                    room.available_count < (room.booked_count || 0);
                  
                  return (
                    <div 
                      key={idx} 
                      className={cn(
                        "p-4 rounded-lg border",
                        availabilityWarning ? "border-orange-300 bg-orange-50" : "border-slate-200 bg-slate-50"
                      )}
                    >
                      {availabilityWarning && (
                        <div className="mb-3 p-2 bg-orange-100 rounded text-sm text-orange-800 flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4" />
                          Available ({room.available_count}) is less than booked ({room.booked_count})
                        </div>
                      )}
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div>
                          <Label className="text-xs">Category</Label>
                          <Select 
                            value={room.category} 
                            onValueChange={(v) => updateRoomOption(idx, 'category', v)}
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="room">Hotel Room</SelectItem>
                              <SelectItem value="camping">Camping Pitch</SelectItem>
                              <SelectItem value="day_pass">Day Pass</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div>
                          <Label className="text-xs">Type Name</Label>
                          <Input
                            value={room.type}
                            onChange={(e) => updateRoomOption(idx, 'type', e.target.value)}
                            placeholder={room.category === 'camping' ? 'e.g. Tent Pitch' : 'e.g. Double Room'}
                            className="h-9"
                          />
                        </div>
                        
                        {room.category === 'camping' || room.pricing_type === 'per_unit' ? (
                          <div>
                            <Label className="text-xs">Price per Unit (£)</Label>
                            <Input
                              type="number"
                              value={room.price_per_unit}
                              onChange={(e) => updateRoomOption(idx, 'price_per_unit', parseFloat(e.target.value) || 0)}
                              className="h-9"
                            />
                          </div>
                        ) : (
                          <div>
                            <Label className="text-xs">Price per Person (£)</Label>
                            <Input
                              type="number"
                              value={room.price_per_person}
                              onChange={(e) => updateRoomOption(idx, 'price_per_person', parseFloat(e.target.value) || 0)}
                              className="h-9"
                            />
                          </div>
                        )}
                        
                        <div>
                          <Label className="text-xs">Available</Label>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              value={room.available_count ?? ''}
                              onChange={(e) => updateRoomOption(idx, 'available_count', e.target.value ? parseInt(e.target.value) : null)}
                              placeholder="∞"
                              className="h-9"
                            />
                            {room.booked_count > 0 && (
                              <span className="text-xs text-slate-500 whitespace-nowrap">
                                ({room.booked_count} booked)
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                        <div>
                          <Label className="text-xs">Max Occupancy</Label>
                          <Input
                            type="number"
                            value={room.max_occupancy ?? ''}
                            onChange={(e) => updateRoomOption(idx, 'max_occupancy', e.target.value ? parseInt(e.target.value) : null)}
                            placeholder="Auto"
                            className="h-9"
                          />
                        </div>
                        
                        <div className="md:col-span-2">
                          <Label className="text-xs">Description</Label>
                          <Input
                            value={room.description}
                            onChange={(e) => updateRoomOption(idx, 'description', e.target.value)}
                            placeholder="Optional description"
                            className="h-9"
                          />
                        </div>
                        
                        <div className="flex items-end justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={room.includes_ticket}
                              onCheckedChange={(checked) => updateRoomOption(idx, 'includes_ticket', checked)}
                            />
                            <Label className="text-xs">Includes Ticket</Label>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeRoomOption(idx)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </SettingsSection>

        {/* ============== 5. TICKETS (Festival only) ============== */}
        {(generalForm.event_type === 'festival' || ticketsForm.ticket_options.length > 0) && (
          <SettingsSection
            id="tickets"
            title="Tickets Configuration"
            description="Configure standalone ticket options (festivals)"
            icon={CreditCard}
            hasChanges={hasTicketsChanges}
            saving={savingSection === 'Tickets'}
            onSave={saveTickets}
          >
            <div className="space-y-4">
              <div className="flex justify-end">
                <Button variant="outline" size="sm" onClick={addTicketOption}>
                  <CreditCard className="w-4 h-4 mr-1" /> Add Ticket Type
                </Button>
              </div>
              
              {ticketsForm.ticket_options.length === 0 ? (
                <div className="text-center py-6 bg-slate-50 rounded-lg">
                  <p className="text-slate-600">No ticket types configured</p>
                  <p className="text-sm text-slate-500">Add ticket types for day passes or festival entry</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {ticketsForm.ticket_options.map((ticket, idx) => (
                    <div key={idx} className="p-3 rounded-lg border border-slate-200 bg-slate-50">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div>
                          <Label className="text-xs">Type Name</Label>
                          <Input
                            value={ticket.type}
                            onChange={(e) => updateTicketOption(idx, 'type', e.target.value)}
                            placeholder="e.g. Weekend Pass"
                            className="h-9"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Price (£)</Label>
                          <Input
                            type="number"
                            value={ticket.price}
                            onChange={(e) => updateTicketOption(idx, 'price', parseFloat(e.target.value) || 0)}
                            className="h-9"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Available</Label>
                          <Input
                            type="number"
                            value={ticket.available_count ?? ''}
                            onChange={(e) => updateTicketOption(idx, 'available_count', e.target.value ? parseInt(e.target.value) : null)}
                            placeholder="∞"
                            className="h-9"
                          />
                        </div>
                        <div className="flex items-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeTicketOption(idx)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      {ticket.booked_count > 0 && (
                        <p className="text-xs text-slate-500 mt-2">
                          {ticket.booked_count} ticket(s) sold
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </SettingsSection>
        )}

        {/* ============== 6. ADD-ONS CONFIGURATION ============== */}
        <SettingsSection
          id="addons"
          title="Add-ons Configuration"
          description="Configure available extras (e.g., coach travel)"
          icon={Bus}
          hasChanges={hasAddOnsChanges}
          saving={savingSection === 'Add-ons'}
          onSave={saveAddOns}
        >
          {hasBookings && hasAddOnsChanges && (
            <WarningBanner>
              Changes to add-on prices only affect <strong>new bookings</strong>. 
              Existing bookings retain their original pricing.
            </WarningBanner>
          )}
          
          <div className="space-y-4">
            <div className="flex justify-end">
              <Button variant="outline" size="sm" onClick={addAddOn}>
                <Bus className="w-4 h-4 mr-1" /> Add Add-on
              </Button>
            </div>
            
            {addOnsForm.add_ons.length === 0 ? (
              <div className="text-center py-6 bg-slate-50 rounded-lg">
                <Bus className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                <p className="text-slate-600">No add-ons configured</p>
                <p className="text-sm text-slate-500">Add extras like coach travel, meals, etc.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {addOnsForm.add_ons.map((addon, idx) => (
                  <div key={idx} className="p-3 rounded-lg border border-slate-200 bg-slate-50">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="md:col-span-1">
                        <Label className="text-xs">Name</Label>
                        <Input
                          value={addon.name}
                          onChange={(e) => updateAddOn(idx, 'name', e.target.value)}
                          placeholder="e.g. Coach Travel"
                          className="h-9"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Price (£/person)</Label>
                        <Input
                          type="number"
                          value={addon.price}
                          onChange={(e) => updateAddOn(idx, 'price', parseFloat(e.target.value) || 0)}
                          className="h-9"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Deposit (£/person)</Label>
                        <Input
                          type="number"
                          value={addon.deposit_amount}
                          onChange={(e) => updateAddOn(idx, 'deposit_amount', parseFloat(e.target.value) || 0)}
                          className="h-9"
                        />
                      </div>
                      <div className="flex items-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeAddOn(idx)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </SettingsSection>

        {/* ============== 7. PAYMENT PLANS CONFIGURATION ============== */}
        <SettingsSection
          id="payment-plans"
          title="Payment Plans Configuration"
          description="Configure payment plan availability dates"
          icon={CalendarClock}
          hasChanges={hasPaymentPlanChanges}
          saving={savingSection === 'Payment Plans'}
          onSave={savePaymentPlan}
        >
          {hasBookings && hasPaymentPlanChanges && (
            <WarningBanner>
              Changes to payment plan dates only affect <strong>new plans</strong>. 
              Existing payment plans will continue with their current schedule.
            </WarningBanner>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Payment Plan Start Date</Label>
              <Input
                type="date"
                value={paymentPlanForm.payment_plan_start_date}
                onChange={(e) => setPaymentPlanForm(prev => ({ ...prev, payment_plan_start_date: e.target.value }))}
              />
              <p className="text-xs text-slate-500 mt-1">
                First payment date for new plans
              </p>
            </div>
            
            <div>
              <Label>Payment Plan End Date</Label>
              <Input
                type="date"
                value={paymentPlanForm.payment_plan_end_date}
                onChange={(e) => setPaymentPlanForm(prev => ({ ...prev, payment_plan_end_date: e.target.value }))}
              />
              <p className="text-xs text-slate-500 mt-1">
                All plans must complete by this date (usually weeks before event)
              </p>
            </div>
          </div>
        </SettingsSection>

        {/* ============== 8. PUBLISHING & EMBEDDING ============== */}
        <SettingsSection
          id="publishing"
          title="Publishing & Embedding"
          description="Control visibility and embedding options"
          icon={Globe}
          hasChanges={hasPublishingChanges}
          saving={savingSection === 'Publishing'}
          onSave={savePublishing}
        >
          <div className="space-y-6">
            {/* Published Status */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <div className="flex items-center gap-2">
                  {publishingForm.is_published ? (
                    <Eye className="w-5 h-5 text-emerald-600" />
                  ) : (
                    <EyeOff className="w-5 h-5 text-slate-400" />
                  )}
                  <Label className="text-base font-medium">
                    {publishingForm.is_published ? 'Published' : 'Unpublished (Draft)'}
                  </Label>
                </div>
                <p className="text-sm text-slate-500 mt-1 ml-7">
                  {publishingForm.is_published 
                    ? 'Event is visible to the public and bookings can be made' 
                    : 'Event is hidden from public view'}
                </p>
              </div>
              <Switch
                checked={publishingForm.is_published}
                onCheckedChange={(checked) => setPublishingForm(prev => ({ ...prev, is_published: checked }))}
              />
            </div>
            
            {hasBookings && !publishingForm.is_published && originalEvent?.is_published && (
              <WarningBanner variant="warning">
                <strong>Warning:</strong> You are about to unpublish an event with {activeBookingsCount} existing booking(s). 
                The event page will become inaccessible to the public.
              </WarningBanner>
            )}
            
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className="font-medium text-slate-900 flex items-center gap-2">
                    <Code className="w-4 h-4 text-violet-600" />
                    Embed Codes
                  </h4>
                  <p className="text-sm text-slate-500">
                    Add booking widgets to external websites
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchEmbedCodes}
                  disabled={loadingEmbed}
                >
                  {loadingEmbed ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-1" />
                  )}
                  {embedCodes ? 'Refresh' : 'Load Embed Codes'}
                </Button>
              </div>
              
              {embedCodes && (
                <div className="space-y-3">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-700">Booking URL</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(embedCodes.booking_url, 'Booking URL')}
                      >
                        <Copy className="w-4 h-4 mr-1" /> Copy
                      </Button>
                    </div>
                    <code className="text-xs text-slate-600 break-all">{embedCodes.booking_url}</code>
                  </div>
                  
                  {Object.entries(embedCodes.embed_codes || {}).map(([key, code]) => (
                    <div key={key} className="p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-slate-700 capitalize">
                          {key.replace('_', ' ')} Embed
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(code, `${key} embed code`)}
                        >
                          <Copy className="w-4 h-4 mr-1" /> Copy
                        </Button>
                      </div>
                      <pre className="text-xs text-slate-600 bg-white p-2 rounded border overflow-x-auto max-h-24">
                        {code}
                      </pre>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </SettingsSection>

        {/* ============== 9. ADVANCED / MAINTENANCE ============== */}
        <SettingsSection
          id="advanced"
          title="Advanced"
          description="Maintenance and diagnostic tools"
          icon={Wrench}
          variant="warning"
        >
          <div className="space-y-4">
            {/* Diagnostics */}
            <div className="p-4 bg-white border border-slate-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-slate-900">Event Diagnostics</h4>
                  <p className="text-sm text-slate-500">Check configuration for issues</p>
                </div>
                <Button
                  variant="outline"
                  onClick={fetchDiagnostics}
                  disabled={loadingDiagnostics}
                >
                  {loadingDiagnostics ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-1" />
                  ) : (
                    <AlertCircle className="w-4 h-4 mr-1" />
                  )}
                  Run Diagnostics
                </Button>
              </div>
              
              {diagnostics && (
                <div className="mt-4 p-3 bg-slate-50 rounded-lg text-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className={diagnostics.status === 'OK' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                      {diagnostics.status}
                    </Badge>
                    <span className="text-slate-600">{diagnostics.title}</span>
                  </div>
                  
                  {diagnostics.issues?.length > 0 && (
                    <div className="space-y-1 mb-2">
                      {diagnostics.issues.map((issue, idx) => (
                        <p key={idx} className="text-red-600">{issue}</p>
                      ))}
                    </div>
                  )}
                  
                  {diagnostics.warnings?.length > 0 && (
                    <div className="space-y-1 mb-2">
                      {diagnostics.warnings.map((warning, idx) => (
                        <p key={idx} className="text-orange-600">{warning}</p>
                      ))}
                    </div>
                  )}
                  
                  {diagnostics.info?.length > 0 && (
                    <div className="space-y-1">
                      {diagnostics.info.map((info, idx) => (
                        <p key={idx} className="text-slate-600">{info}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Recalculate Availability */}
            <div className="p-4 bg-white border border-orange-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-slate-900">Recalculate Room Availability</h4>
                  <p className="text-sm text-slate-500">
                    Fix mismatches between room counts and actual bookings
                  </p>
                </div>
                <Button
                  variant="outline"
                  onClick={() => setConfirmDialog({ 
                    open: true, 
                    type: 'recalculate', 
                    data: null 
                  })}
                  className="border-orange-300 text-orange-700 hover:bg-orange-50"
                >
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Recalculate
                </Button>
              </div>
            </div>
          </div>
        </SettingsSection>

        {/* ============== 10. DANGER ZONE ============== */}
        <SettingsSection
          id="danger"
          title="Danger Zone"
          description="Destructive actions that cannot be undone"
          icon={Trash2}
          variant="danger"
        >
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start justify-between">
              <div>
                <h4 className="font-medium text-red-800">Delete This Weekender</h4>
                <p className="text-sm text-red-600 mt-1">
                  Permanently delete this event and all associated configuration.
                  {hasBookings && (
                    <span className="font-medium"> This event has {activeBookingsCount} active booking(s) and cannot be deleted.</span>
                  )}
                </p>
              </div>
              <Button
                variant="outline"
                onClick={() => setDeleteConfirm(true)}
                disabled={hasBookings}
                className="border-red-300 text-red-700 hover:bg-red-100 disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Delete Event
              </Button>
            </div>
          </div>
        </SettingsSection>
      </div>

      {/* ============== CONFIRMATION DIALOGS ============== */}
      
      {/* General Confirmation Dialog */}
      <AlertDialog 
        open={confirmDialog.open && confirmDialog.type !== 'recalculate'} 
        onOpenChange={(open) => !open && setConfirmDialog({ open: false, type: null, data: null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Changes</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDialog.type === 'pricing' && (
                <>
                  You are changing deposit amounts on an event with <strong>{activeBookingsCount} existing booking(s)</strong>.
                  <br /><br />
                  These changes will only affect <strong>new bookings</strong>. Existing bookings will not be recalculated.
                </>
              )}
              {confirmDialog.type === 'rooms' && (
                <>
                  You are changing room configuration on an event with <strong>{activeBookingsCount} existing booking(s)</strong>.
                  <br /><br />
                  Price changes will only affect <strong>new bookings</strong>. Existing bookings retain their original pricing.
                </>
              )}
              {confirmDialog.type === 'addons' && (
                <>
                  You are changing add-on configuration on an event with <strong>{activeBookingsCount} existing booking(s)</strong>.
                  <br /><br />
                  These changes will only affect <strong>new bookings</strong>.
                </>
              )}
              {confirmDialog.type === 'paymentplan' && (
                <>
                  You are changing payment plan dates on an event with <strong>{activeBookingsCount} existing booking(s)</strong>.
                  <br /><br />
                  Existing payment plans will continue with their current schedule.
                </>
              )}
              {confirmDialog.type === 'unpublish' && (
                <>
                  You are about to <strong>unpublish</strong> an event with <strong>{activeBookingsCount} existing booking(s)</strong>.
                  <br /><br />
                  The event page will become inaccessible to the public. Existing customers will not be able to view or manage their bookings via the public URL.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={executeConfirmedSave}>
              Save Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Recalculate Confirmation */}
      <AlertDialog 
        open={confirmDialog.open && confirmDialog.type === 'recalculate'} 
        onOpenChange={(open) => !open && setConfirmDialog({ open: false, type: null, data: null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Recalculate Room Availability</AlertDialogTitle>
            <AlertDialogDescription>
              This will recalculate room availability counts based on actual bookings.
              <br /><br />
              This action is safe and can fix mismatches caused by room amendments.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              setConfirmDialog({ open: false, type: null, data: null });
              recalculateAvailability();
            }}>
              Recalculate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteConfirm} onOpenChange={setDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">Delete Weekender Event</AlertDialogTitle>
            <AlertDialogDescription>
              This action <strong>cannot be undone</strong>. This will permanently delete:
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>The event "{event.title}"</li>
                <li>All room configuration</li>
                <li>All add-on configuration</li>
                <li>All event settings</li>
              </ul>
              <div className="mt-4">
                <Label>Type <strong>{event.title}</strong> to confirm:</Label>
                <Input
                  value={deleteInput}
                  onChange={(e) => setDeleteInput(e.target.value)}
                  placeholder="Type event title here"
                  className="mt-2"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteInput('')}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteEvent}
              disabled={deleteInput !== event.title}
              className="bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Delete Event
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
